import { ErrorPage } from '@/components/ErrorPage';
import { UploadFileModal } from '@/components/FilesModals';
import { TablePageLayout } from '@/layouts/TablePageLayout';
import { formatRelativeTime } from '@/utils/format';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import FileOutlined from '@ant-design/icons/FileOutlined';
import MoreOutlined from '@ant-design/icons/MoreOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Dropdown from 'antd/es/dropdown';
import type { MenuProps } from 'antd/es/menu';
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { DataTable, useDataTableState } from 'ui-components';
import type { DataTableColumnType } from 'ui-components';

import {
  type GetFilesResponse,
  downloadFile,
  useDeleteFile,
  useFiles,
} from '../../../queries/files';

type FileItem = GetFilesResponse['items'][0];

const FileNameLink = styled(Link)`
  ${({ theme }) => `
  font-weight: ${theme.fontWeightStrong};
  text-decoration: none;
  color: ${theme.colorLink};

  &:hover {
    color: ${theme.colorLinkHover};
  }
`}
`;

const ActionButton = styled(Button)`
  ${({ theme }) => `
  width: ${theme.controlHeight}px;
  height: ${theme.controlHeight}px;
`}
`;

export const FilesPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { message, modal } = App.useApp();

  // Modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { requestState } = useDataTableState({
    filterMap: {
      fileName: 'nameLike',
    },
  });

  const {
    data: filesData,
    isLoading,
    error,
  } = useFiles(
    {
      ...requestState,
      teamIds: teamId ? [teamId] : undefined,
    },
    {
      enabled: !!teamId,
    },
  );

  const deleteFileMutation = useDeleteFile();

  const handleUploadFile = () => {
    setIsUploadModalOpen(true);
  };

  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      void message.success('ID copied to clipboard');
    } catch (error) {
      void message.error('Failed to copy ID');
    }
  };

  const handleDownloadFile = async (file: FileItem) => {
    try {
      await downloadFile(file.fileId, file.fileName);
      void message.success('File download started');
    } catch (error) {
      void message.error('Failed to download file');
    }
  };

  const handleDeleteFile = (file: FileItem) => {
    void modal.confirm({
      title: 'Delete File',
      content: `Are you sure you want to delete "${file.fileName}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteFileMutation.mutateAsync(file.fileId);
          void message.success(
            `File "${file.fileName}" has been deleted successfully`,
          );
        } catch (error) {
          void message.error('Failed to delete file');
        }
      },
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const columns: DataTableColumnType<FileItem>[] = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'fileName',
        key: 'fileName',
        ellipsis: true,
        render: (_, record) => (
          <FileNameLink to={`/team/${teamId}/files/${record.fileId}`}>
            {record.fileName}
          </FileNameLink>
        ),
        sorter: true,
        filter: {
          type: 'search',
          placeholder: 'Search by name...',
        },
      },
      {
        title: 'Type',
        dataIndex: 'mimeType',
        key: 'mimeType',
        width: 150,
        ellipsis: true,
      },
      {
        title: 'Size',
        dataIndex: 'fileSize',
        key: 'fileSize',
        width: 100,
        align: 'right',
        render: (size: number) => formatFileSize(size),
        sorter: true,
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150,
        render: (date: Date) => formatRelativeTime(date),
        sorter: true,
      },
      {
        title: 'Last Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 150,
        render: (date: Date) => formatRelativeTime(date),
        sorter: true,
      },
      {
        title: '',
        key: 'actions',
        align: 'center',
        width: 80,
        render: (_, record) => {
          const items: MenuProps['items'] = [
            {
              key: 'download',
              icon: <DownloadOutlined />,
              label: 'Download',
              onClick: () => handleDownloadFile(record),
            },
            {
              key: 'copy-id',
              icon: <CopyOutlined />,
              label: 'Copy ID',
              onClick: () => void handleCopyId(record.fileId),
            },
            {
              type: 'divider',
            },
            {
              key: 'delete',
              icon: <DeleteOutlined />,
              label: 'Delete File',
              danger: true,
              onClick: () => handleDeleteFile(record),
            },
          ];

          return (
            <Dropdown
              menu={{ items }}
              trigger={['click']}
              placement="bottomRight"
            >
              <ActionButton type="text" icon={<MoreOutlined />} size="small" />
            </Dropdown>
          );
        },
      },
    ],
    [teamId],
  );

  // Check for missing teamId after all hooks
  if (!teamId) {
    return (
      <ErrorPage
        title="Team Not Found"
        message="Unable to load files. Please make sure you're accessing a valid team."
        backButtonText="Go to Teams"
        onBack={() => (window.location.href = '/')}
      />
    );
  }

  if (error) {
    return (
      <div>
        <h2>Error Loading Files</h2>
        <p>There was an error loading the files. Please try again.</p>
      </div>
    );
  }

  return (
    <>
      <TablePageLayout
        title="Files"
        description="Manage and organize your team's files."
        actions={[
          {
            label: 'Upload File',
            type: 'primary',
            icon: <UploadOutlined />,
            onClick: handleUploadFile,
          },
        ]}
      >
        <DataTable<FileItem>
          columns={columns}
          data={filesData?.items ?? []}
          loading={isLoading}
          rowKey="fileId"
          maxVisibleFilters={3}
          pagination={{
            current: filesData?.pagination?.page ?? 1,
            pageSize: filesData?.pagination?.pageSize ?? 20,
            total: filesData?.pagination?.totalItems ?? 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} files`,
          }}
          emptyState={{
            icon: <FileOutlined />,
            title: 'No files found',
            description: 'Upload your first file to get started.',
            action: {
              label: 'Upload Your First File',
              onClick: handleUploadFile,
              icon: <UploadOutlined />,
            },
            overrideOnFiltersPresent: {
              title: 'No files match your filters',
              description: 'Try adjusting your filters to see more results.',
              action: undefined,
            },
          }}
        />
      </TablePageLayout>
      <UploadFileModal
        open={isUploadModalOpen}
        onCancel={() => setIsUploadModalOpen(false)}
        teamId={teamId || ''}
        onSuccess={() => {
          setIsUploadModalOpen(false);
        }}
      />
    </>
  );
};
